import * as React from 'react';
import { Search, Database, Brain, FileBarChart, Sparkles, RefreshCw, FileText, BellRing } from 'lucide-react';
import {
  Home as HomeIcon,
  Users as UsersIcon,
  Store,
  ColumnsThree,
  Notification,
  Taxonomy,
  Edit,
  Warning,
  ChartLine,
  ChartBar,
  Publisher,
  Select as SelectIcon,
  Upload,
  Speedometor,
  Settings,
  Kitchen,
  History,
  InternalUser,
  Video,
  App,
} from 'lift-ui';
import { IconMailFast } from '@tabler/icons-react';
import { Dialog, DialogPortal, DialogOverlay } from '@/components/ui/dialog';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { cn } from '@/lib/utils';
import { Command as CommandPrimitive } from 'cmdk';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandItem,
  CommandList,
  CommandSeparator,
  CommandShortcut,
} from '@/components/ui/command';

interface NavItem {
  id: string;
  name: string;
  section: string;
  group?: string;
  icon: React.ReactNode;
}

const IS_DEV = (import.meta as any).env?.VITE_ENVIRONMENT === 'development';

const ALL_NAV_ITEMS: NavItem[] = [
  // General
  { id: 'overview', name: 'Overview', section: 'General', icon: <HomeIcon /> },
  { id: 'users-internal', name: 'Users', section: 'General', icon: <UsersIcon /> },
  { id: 'organization-down', name: 'Organizations', section: 'General', icon: <App /> },
  { id: 'brands', name: 'Brands', section: 'General', icon: <Store /> },
  { id: 'drawer-structure-v2', name: 'Drawer Structure', section: 'General', icon: <ColumnsThree /> },
  { id: 'notification-teams', name: 'Notification Teams', section: 'General', icon: <Notification /> },
  { id: 'user-analytics-v2', name: 'LIFT User Analytics', section: 'General', group: 'LIFT Analytics', icon: <HomeIcon /> },
  { id: 'lift-ai-v2', name: 'LIFT AI Dashboard', section: 'General', group: 'LIFT Analytics', icon: <Sparkles /> },
  // Affiliate
  { id: 'crm-user-analytics', name: 'CRM User Analytics', section: 'Affiliate', icon: <ChartLine /> },
  { id: 'br-wt', name: 'Brand Weights', section: 'Affiliate', icon: <ChartBar /> },
  { id: 'pub-map', name: 'Pub Mapping', section: 'Affiliate', icon: <Publisher /> },
  { id: 'goal-matrix', name: 'Goal Matrix', section: 'Affiliate', icon: <SelectIcon /> },
  { id: 'pitching-tool', name: 'Pitching Tool Dashboard', section: 'Affiliate', icon: <IconMailFast size={18} /> },
  { id: 'ga4-config', name: 'GA4 Configuration', section: 'Affiliate', icon: <ChartLine /> },
  { id: 'imports', name: 'Affluent Imports', section: 'Affiliate', icon: <Upload /> },
  // Performance
  { id: 'performance', name: 'Media Platform Accounts', section: 'Performance', icon: <Store /> },
  { id: 'report-configuration', name: 'Report Configuration', section: 'Performance', icon: <FileBarChart size={18} /> },
  { id: 'trigger', name: 'Taxonomy Import Triggers', section: 'Performance', icon: <RefreshCw /> },
  { id: 'adverity-logs', name: 'Adverity Logs', section: 'Performance', icon: <FileText /> },
  { id: 'onboarding-logs', name: 'Onboarding Logs', section: 'Performance', icon: <FileText /> },
  { id: 'pacing-looker-dashboard', name: 'Pacing Rollup Dashboard', section: 'Performance', icon: <Speedometor /> },
  { id: 'taxonomy-merge', name: 'Merge Taxonomy Column', section: 'Performance', group: 'Taxonomy', icon: <Taxonomy /> },
  { id: 'taxonomy-name', name: 'Naming Taxonomy', section: 'Performance', group: 'Taxonomy', icon: <Edit /> },
  { id: 'engen-exceptions', name: 'Engen Exception', section: 'Performance', group: 'Taxonomy', icon: <Warning /> },
  // Creative
  { id: 'brand-params-mo', name: 'Brand Info MO', section: 'Creative', icon: <Settings /> },
  { id: 'pivot-content-kitchen', name: 'Kitchen Buddies', section: 'Creative', icon: <Kitchen /> },
  // Admin / Settings
  { id: 'request-list', name: 'Requests List', section: 'Admin / Settings', icon: <History /> },
  { id: 'bigquery-metadata', name: 'BigQuery Metadata', section: 'Admin / Settings', icon: <Database /> },
  { id: 'iam-support', name: 'IAM Support', section: 'Admin / Settings', icon: <InternalUser /> },
  { id: 'playwright-trigger', name: 'Triggers', section: 'Admin / Settings', icon: <Video /> },
  { id: 'lift-ai-models', name: 'Lift AI Models', section: 'Admin / Settings', icon: <Brain /> },
  { id: 'dmg-alerts', name: 'DMG Alerts', section: 'Admin / Settings', icon: <BellRing size={18} /> },
];

const NAV_ITEMS: NavItem[] = IS_DEV
  ? ALL_NAV_ITEMS
  : ALL_NAV_ITEMS.filter((item) => item.id !== 'bigquery-metadata');

const SECTIONS = Array.from(new Set(NAV_ITEMS.map((item) => item.section)));

// Stable lookup map: value string (used by cmdk) → item id
const VALUE_TO_ID = new Map<string, string>();
NAV_ITEMS.forEach((item) => {
  VALUE_TO_ID.set(item.id, item.id);
});

interface GlobalCommandPaletteProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onNavigate: (id: string) => void;
}

export function GlobalCommandPalette({ open, onOpenChange, onNavigate }: GlobalCommandPaletteProps) {
  const [query, setQuery] = React.useState('');

  React.useEffect(() => {
    if (!open) setQuery('');
  }, [open]);

  const filtered = query.trim()
    ? NAV_ITEMS.filter((item) =>
        item.name.toLowerCase().includes(query.toLowerCase()) ||
        item.section.toLowerCase().includes(query.toLowerCase()) ||
        (item.group?.toLowerCase().includes(query.toLowerCase()) ?? false)
      )
    : NAV_ITEMS;

  const groupedFiltered = SECTIONS.map((section) => ({
    section,
    items: filtered.filter((item) => item.section === section),
  })).filter((g) => g.items.length > 0);

  const handleSelect = (itemId: string) => {
    onNavigate(itemId);
    onOpenChange(false);
  };

  const isMac = typeof navigator !== 'undefined' && /Mac|iPod|iPhone|iPad/.test(navigator.userAgent);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogPortal>
        <DialogOverlay />
        <DialogPrimitive.Content
          className={cn(
            'fixed left-[50%] top-[50%] z-50 w-full max-w-lg translate-x-[-50%] translate-y-[-50%] overflow-hidden rounded-lg border bg-popover p-0 shadow-lg',
            'duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%]'
          )}
        >
          <Command
            className="[&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:font-medium [&_[cmdk-group-heading]]:text-muted-foreground [&_[cmdk-group]:not([hidden])_~[cmdk-group]]:pt-0 [&_[cmdk-group]]:px-2 [&_[cmdk-input-wrapper]_svg]:h-5 [&_[cmdk-input-wrapper]_svg]:w-5 [&_[cmdk-input]]:h-12 [&_[cmdk-item]]:px-3 [&_[cmdk-item]]:py-2 [&_[cmdk-item]_svg]:h-4 [&_[cmdk-item]_svg]:w-4"
          >
            <div className="flex items-center border-b px-3" cmdk-input-wrapper="">
              <Search className="mr-2 h-4 w-4 shrink-0 opacity-50" />
              <CommandPrimitive.Input
                className="flex h-11 w-full bg-transparent py-3 text-sm outline-none placeholder:text-muted-foreground disabled:cursor-not-allowed disabled:opacity-50"
                placeholder="Search pages…"
                value={query}
                onValueChange={setQuery}
              />
              <span className="text-muted-foreground/60 text-sm font-medium tracking-wide whitespace-nowrap">
                {isMac ? '⌘ + K' : 'Ctrl + K'}
              </span>
            </div>
            <CommandList>
              <CommandEmpty>No results found.</CommandEmpty>
              {groupedFiltered.map((g, idx) => (
                <React.Fragment key={g.section}>
                  {idx > 0 && <CommandSeparator />}
                  <CommandGroup heading={g.section}>
                    {g.items.map((item) => (
                      <CommandItem
                        key={item.id}
                        value={item.id}
                        onSelect={handleSelect}
                        className="flex items-center gap-2 cursor-pointer"
                        keywords={[item.name, item.section, item.group ?? '']}
                      >
                        <span className="flex h-4 w-4 shrink-0 items-center justify-center">{item.icon}</span>
                        <span className="flex-1 truncate">{item.name}</span>
                        {item.group && (
                          <CommandShortcut>{item.group}</CommandShortcut>
                        )}
                      </CommandItem>
                    ))}
                  </CommandGroup>
                </React.Fragment>
              ))}
            </CommandList>
          </Command>
        </DialogPrimitive.Content>
      </DialogPortal>
    </Dialog>
  );
}

export default GlobalCommandPalette;
